<template>
	<section
		v-if="items.length"
		class="consult-service-m"
		:class="{ 'consult-service-m--area': isAreaTeacher }"
	>
		<ul class="consult-service-m__list" role="list">
			<li
				v-for="(item, index) in items"
				:key="item.id"
				:ref="el => bindServiceImpression(el, item, index)"
				class="consult-card"
				role="listitem"
				@click="toServiceDetail(item, index)"
			>
				<div v-if="isAreaTeacher" class="consult-card__image-wrap">
					<img class="consult-card__image" :src="item.cover || defaultCover" :alt="item.name" />
				</div>

				<div class="consult-card__body">
					<divs class="consult-card__name">{{ item.name }}</divs>
					<p class="consult-card__detail">{{ item.detail }}</p>

					<p v-if="item.discount_tag" class="consult-card__original">
						{{ t(`${I18N_NS}.originalPrice`, { price: item.price }) }}
					</p>

					<footer class="consult-card__footer">
						<div class="consult-card__price">
							<img src="~/assets/images/pc/coin.png" :alt="t(`${I18N_NS}.coinAlt`)" />
							<span>{{ item.final_price }}</span>
							<em v-if="item.discount_tag">{{ t(`${I18N_NS}.finalPrice`) }}</em>
						</div>

						<button
							type="button"
							class="consult-card__btn"
							:class="{ 'consult-card__btn--icon': isAreaTeacher }"
							:aria-label="t(`${I18N_NS}.orderNow`)"
							@click.stop="order(item, index)"
						>
							<span v-if="isAreaTeacher" class="consult-card__plus" aria-hidden="true"></span>
							<template v-else>{{ t(`${I18N_NS}.orderNow`) }}</template>
						</button>
					</footer>
				</div>
			</li>
		</ul>
	</section>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, ref } from "vue";
import { orderConsultant } from "~/api/api";
import { PayBox } from "~/hooks/pay/index";
import { useI18n } from "~/composables/useI18n";
import { useInjectedMentorDetailTracking } from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";
import defaultCover from "~/assets/images/default.png";

const I18N_NS = "teacherDetail.pc";
const { t, locale } = useI18n();
const openLogin = inject("openLogin");
const tracking = useInjectedMentorDetailTracking();
const impressionTracker = tracking?.createImpressionTracker?.();

const props = defineProps({
	data: {
		type: Array,
		default: () => [],
	},
	variant: {
		type: String,
		default: "default",
	},
});

const route = useRoute();
const isOrder = ref(false);
const isAreaTeacher = computed(() => props.variant === "area");
const items = computed(() => (Array.isArray(props.data) ? props.data.map(formatServiceItem) : []));

function bindServiceImpression(el, item, index) {
	if (!el || !impressionTracker || !item?.id) return;
	impressionTracker.bind(el, `service_${item.id}`, "mentor_service_card_impression", {
		service_id: String(item.id),
		price_bucket: tracking.getPriceBucket(item.final_price ?? item.price),
		position: index + 1,
	});
}

const toServiceDetail = async (item, index) => {
	tracking?.track?.("mentor_service_card_click", {
		service_id: String(item.id),
		price_bucket: tracking.getPriceBucket(item.final_price ?? item.price),
		action_type: "card",
		position: typeof index === "number" ? index + 1 : undefined,
	});
	await navigateTo(`/seer/teacher-service/${route.params.id}/${item.id}`);
};

const order = async (item, index) => {
	openLogin?.();
	if (isOrder.value) return;
	isOrder.value = true;

	try {
		const res = await orderConsultant({
			teacher_uid: route.params.id,
			service_id: item.id,
		});

		if (import.meta.client) {
			const { logEvent } = await import("~/utils/firebase");
			logEvent("site_masterpage_pay");
		}
		tracking?.track?.("mentor_service_card_click", {
			service_id: String(item.id),
			price_bucket: tracking.getPriceBucket(item.final_price ?? item.price),
			action_type: "order",
			position: typeof index === "number" ? index + 1 : undefined,
		});

		PayBox.alter({
			orderInfo: res.data,
			finalPrice: res.data.final_amount,
		});
	} catch (error) {
		console.error("Order failed:", error);
	} finally {
		isOrder.value = false;
	}
};

onBeforeUnmount(() => {
	impressionTracker?.reset?.();
});

function formatServiceItem(item) {
	return {
		...item,
		name: pickLocalized(item, "name"),
		detail: pickLocalizedDetail(item),
		cover: pickServiceImage(item),
	};
}

function pickLocalized(item, key) {
	const isEn = locale.value === "en";
	return (isEn ? item?.[`${key}_en`] : item?.[key]) || item?.[key] || "";
}

function pickLocalizedDetail(item) {
	if (locale.value === "en") {
		return (
			item?.detail_en ||
			item?.description_en ||
			item?.intro_en ||
			item?.detail ||
			item?.description ||
			item?.intro ||
			""
		);
	}
	return item?.detail || item?.description || item?.intro || "";
}

function pickServiceImage(item) {
	const candidates =
		locale.value === "en"
			? [item?.image, item?.background_image, item?.image_jt]
			: [item?.image_jt, item?.image, item?.background_image];
	return candidates.map(getFirstImageUrl).find(Boolean) || "";
}

function getFirstImageUrl(value) {
	if (Array.isArray(value)) {
		return value.map(getFirstImageUrl).find(Boolean) || "";
	}
	if (typeof value !== "string") return "";
	return value
		.split(",")
		.map(item => item.trim())
		.find(Boolean);
}
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	overflow-wrap: anywhere;
}

.consult-service-m {
	width: 100%;
	padding: 0 32px;
	font-family: "Source Han Serif CN", "PingFang SC", sans-serif;

	&__list {
		display: grid;
		grid-template-columns: 1fr;
		gap: 24px;
		margin: 0;
		padding: 0;
		list-style: none;
	}
}

.consult-card {
	min-width: 0;
	padding: 24px;
	border-radius: 12px;
	background: #f7f7f7;
	cursor: pointer;

	&__image-wrap {
		display: none;
	}

	&__body {
		display: flex;
		min-width: 0;
		flex-direction: column;
	}

	&__name {
		margin: 0;
		color: #333333;
		font-size: 32px;
		font-weight: 700;
		line-height: 1.35;
		@include line-clamp(1);
	}

	&__detail {
		margin: 18px 0 22px;
		color: #585858;
		font-family: "PingFang SC", sans-serif;
		font-size: 26px;
		line-height: 40px;
		@include line-clamp(5);
	}

	&__original {
		margin: 0 0 12px;
		color: #999999;
		font-size: 22px;
		line-height: 1.3;
	}

	&__footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		margin-top: auto;
	}

	&__price {
		display: flex;
		align-items: center;
		min-width: 0;
		color: #de2828;

		img {
			flex: 0 0 auto;
			width: 32px;
			height: 32px;
			margin-right: 6px;
		}

		span {
			font-size: 32px;
			font-weight: 700;
			line-height: 1;
		}

		em {
			margin-left: 8px;
			padding: 2px 8px;
			border-radius: 999px;
			background: #ffe4e2;
			color: #c62000;
			font-size: 20px;
			font-style: normal;
			line-height: 1.4;
			white-space: nowrap;
		}
	}

	&__btn {
		flex: 0 0 auto;
		min-width: 86px;
		height: 48px;
		padding: 0 18px;
		border: 0;
		border-radius: 6px;
		background: #333333;
		color: #ffffff;
		font-size: 24px;
		font-weight: 600;
		line-height: 48px;
		white-space: nowrap;
	}

	&__btn--icon {
		position: relative;
		width: 40px;
		min-width: 40px;
		height: 40px;
		padding: 0;
		border-radius: 50%;
	}

	&__plus {
		position: absolute;
		left: 50%;
		top: 50%;
		width: 18px;
		height: 18px;
		transform: translate(-50%, -50%);

		&::before,
		&::after {
			content: "";
			position: absolute;
			left: 50%;
			top: 50%;
			background: #ffffff;
			border-radius: 2px;
			transform: translate(-50%, -50%);
		}

		&::before {
			width: 18px;
			height: 3px;
		}

		&::after {
			width: 3px;
			height: 18px;
		}
	}
}

.consult-service-m--area {
	.consult-service-m__list {
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 20px;
	}

	.consult-card {
		padding: 0;
		border: 1px solid #eeeeee;
		border-radius: 8px;
		background: #ffffff;
		overflow: hidden;
	}

	.consult-card__image-wrap {
		display: block;
		width: 100%;
		aspect-ratio: 1 / 1;
		background: #f3f3f3;
		overflow: hidden;
	}

	.consult-card__image {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	.consult-card__body {
		padding: 16px 16px 18px;
	}

	.consult-card__name {
		font-size: 24px;
		line-height: 1.35;
	}

	.consult-card__detail {
		margin: 10px 0 14px;
		font-size: 22px;
		line-height: 32px;
		@include line-clamp(2);
	}

	.consult-card__price {
		img {
			width: 28px;
			height: 28px;
		}

		span {
			font-size: 28px;
		}
	}
}
</style>
